import {API, getApiPath, getGraphQLPath} from '@/lib/utils/api.utils';
import {CatalogOffer} from 'epic-graphql-types';
import {CurrencyCode, CatalogOfferProduct} from '@/types';

import {
	CATALOG_COUNTRY_QUERY,
	CATALOG_CURRENCY_QUERY,
	CATALOG_OFFER_ITEMS_QUERY,
	CATALOG_OFFER_QUERY,
	CATALOG_SEARCH_QUERY
} from './catalog.query';

import {
	CurrencyRes,
	CatalogCurrencyQueryRes,
	CatalogOfferRes,
	CatalogOfferQueryRes,
	CountryRes,
	CatalogCountryQueryRes,
	CatalogOfferItemsQueryRes,
	CatalogOfferItemsRes,
	CatalogAgeRatingRes,
	SearchStoreQueryRes,
	SearchStoreRes,
	SearchStoreQuery
} from './types';

export const searchStore = async (params: SearchStoreQuery): Promise<SearchStoreRes> => {
	const response = await API.post<SearchStoreQueryRes>(
		getGraphQLPath(),
		JSON.stringify({
			query: CATALOG_SEARCH_QUERY(params)
		})
	);
	return response?.data?.data?.Catalog?.searchStore || {};
};

export const getCurrencyDetail = async (
	currencyCode: CurrencyCode | keyof typeof CurrencyCode
): Promise<CurrencyRes> => {
	const response = await API.post<CatalogCurrencyQueryRes>(
		getGraphQLPath(),
		JSON.stringify({
			query: CATALOG_CURRENCY_QUERY(currencyCode)
		})
	);
	return response?.data?.data?.Catalog?.currency || {};
};

export const getCatalogOffer = async (
	sandboxId: string,
	offerId: string,
	locale: string,
	country: string
): Promise<CatalogOfferRes> => {
	const response = await API.post<CatalogOfferQueryRes>(
		getGraphQLPath(),
		JSON.stringify({
			query: CATALOG_OFFER_QUERY(sandboxId, offerId, locale, country)
		})
	);
	return response?.data?.data?.Catalog?.catalogOffer || {};
};

export const getCatalogOffers = async (
	products: CatalogOfferProduct[],
	locale: string,
	country: string
): Promise<CatalogOffer[]> => {
	const queries = products.flatMap(({sandboxId, offerIds}) =>
		offerIds.map((offerId) => CATALOG_OFFER_QUERY(sandboxId, offerId, locale, country))
	);

	const response = await API.post<CatalogOfferQueryRes[]>(
		getGraphQLPath(),
		JSON.stringify(queries.map((query) => ({query})))
	);

	const values = response?.data?.map((resp) => resp.data?.Catalog?.catalogOffer);
	return values || [];
};

export const getCatalogOfferItems = async (
	sandboxId: string,
	offerId: string,
	locale: string
): Promise<CatalogOfferItemsRes> => {
	const response = await API.post<CatalogOfferItemsQueryRes>(
		getGraphQLPath(),
		JSON.stringify({
			query: CATALOG_OFFER_ITEMS_QUERY(sandboxId, offerId, locale)
		})
	);
	return response?.data?.data?.Catalog?.offerSubItems || {};
};

export const getCatalogCountry = async (countryCode: string): Promise<CountryRes | undefined> => {
	const response = await API.post<CatalogCountryQueryRes>(
		getGraphQLPath(),
		JSON.stringify({
			query: CATALOG_COUNTRY_QUERY(countryCode)
		})
	);
	return response?.data?.data?.Catalog?.countryData || {};
};

export const getAgeRating = async (
	locale: string,
	countryCode: string
): Promise<CatalogAgeRatingRes> => {
	const response = await API.get<CatalogAgeRatingRes>(`${getApiPath()}/product/age-rating`, {
		params: {locale, country: countryCode}
	});
	return response?.data || {};
};
